import { Injectable } from '@angular/core';
import { HttpInternalService } from './http.service';
import { environment } from 'src/environments/environment';
import { NewReport } from 'src/app/modules/bookkeeping/models/supplier/new-report';
import { AppConfigService } from 'src/app/appConfig.service';

@Injectable({
  providedIn: 'root'
})
export class UnreconciledReportService {
  private get baseUrl(): string {
    return this.configService.apiURL;
  }
  constructor(
    private _httpService: HttpInternalService,
    private configService: AppConfigService
  ) { }

  getUnreconciledReport(organizationId: string, status: string) {
    return this._httpService.getRequest<any>(`Reporting/getUnreconciledReport?organizationId=${organizationId}&status=${status}`);
  }

  getReportDashboardCounts(organizationId: string) {
    return this._httpService.getRequest<{
      actionRequired: { count: number; reports: { reportId: string; reportName: string; status: number; userId: string; userName: string; submitterId: string; submitterName: string }[] };
      awaitingApproval: { count: number; reports: { reportId: string; reportName: string; status: number; userId: string; userName: string; submitterId: string; submitterName: string }[] };
    }>(`Reporting/getReportDashboardCounts?organizationId=${organizationId}`);
  }

  getUnreconciledUserReport(organizationId: string, reportId: string) {
    return this._httpService.getRequest<any>(`Reporting/getUnreconciledReportUserSummary?organizationId=${organizationId}&reportId=${reportId}`);
  }

  createNewReport(report: NewReport) {
    return this._httpService.postClearRequest<{ reportId: string, message: string }>('Reporting/createNewReport', report);
  }

  /** Lists unreconciled reports for an organisation that are not yet completed. */
  getIncompleteUnreconciledReports(organizationId: string) {
    return this._httpService.getRequest<any>(
      `Reporting/getIncompleteUnreconciledReports?organizationId=${organizationId}`
    );
  }

  GetUnreconciledReportSummary(organisationID: string, status: string) {
    var reporturl = "Reporting/getUnreconciledReportSummary";
    return this._httpService.getRequest<any>(`${reporturl}?organizationId=${organisationID}&status=${status}`);
  }

  AssignReportMarkComplete(reportId: string, approverId: string, userid?: string, organisationId?: string) {
    var reporturl = "Reporting/AssignReportMarkComplete";
    return this._httpService.getRequest<any>(`${reporturl}`, { reportId: reportId, approverId: approverId, userid: userid, organisationId: organisationId });
  }

  AssignReportApproveComplete(reportId: string, userid?: string, organisationId?: string) {
    var reporturl = "Reporting/AssignReportApproveComplete";
    return this._httpService.getRequest<any>(`${reporturl}`, { reportId: reportId, userid: userid, organisationId: organisationId });
  }

  UpdateApprovalStatus(id: string, approved: boolean, organizationId: string) {
    var reporturl = "Reporting/UpdateSuggestionApproval";
    return this._httpService.getRequest<any>(`${reporturl}`, { id: id, approved: approved, organizationId: organizationId });
  }

  changeReportStatus(userObj: any) {
    var reporturl = "Reporting/UpdateReportStatus";
    return this._httpService.getRequest<any>(`${reporturl}`, { id: userObj.id, isCompleted: userObj.isCompleted, organizationId: userObj.organizationId });
  }

  /**
   * Hard-deletes a report and every related transaction. Backend rejects this for
   * Submitter-role users (returns 403); the front-end also gates the menu item on
   * roleId, but the server check is the source of truth.
   */
  deleteReport(reportId: string) {
    return this._httpService.deleteRequest<any>('Reporting/DeleteReport', { reportId });
  }

  /** Renames a report. Admins only: the backend returns 403 for any other role. */
  renameReport(reportId: string, organizationId: string, reportName: string) {
    return this._httpService.postRequest<any>('Reporting/RenameReport',
      JSON.stringify({ ReportId: reportId, OrganisationId: organizationId, ReportName: reportName }));
  }

  /**
   * Reruns suggested matches for a report's open transactions. Admins only: the backend
   * returns 403 for any other role. assignedUserId null means the whole report,
   * unassigned transactions included; autoMatch false only refreshes the suggestions.
   * The matching runs in the background, so the response only says how much was queued.
   */
  rerunSuggestedMatches(reportId: string, organizationId: string, assignedUserId: string | null, autoMatch: boolean) {
    return this._httpService.postRequest<any>('Reporting/RerunSuggestedMatches',
      JSON.stringify({
        ReportId: reportId,
        OrganisationId: organizationId,
        AssignedUserId: assignedUserId || null,
        AutoMatch: autoMatch
      }));
  }
}
